import { gql, useQuery } from "@apollo/client";
import {
  AtlasMenuContentItem,
  Button,
  ButtonCollection,
  ButtonGroup,
  DeprecatedLink,
  Menu,
  TextField,
} from "@resource/atlas";
import { atlasDownload, atlasSearch } from "@resource/atlas/icons";
import { useAuthContext } from "auth/context";
import FakeMultiSelect from "components/FakeMultiSelect";
import {
  GetDataForFeedbackFilters,
  GetDataForFeedbackFiltersVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import { useEventCallback } from "react-hooks/useEventCallback";

import FeedbackTimeRangeFilter from "../FeedbackTimeRangeFilter";

export enum FilterTimeRangeEnum {
  "last1Month" = "Last month",
  "last3Months" = "Last 3 months",
  "last6Months" = "Last 6 months",
  "lastYear" = "Last year",
}

export const DefaultFilterTimeRange = FilterTimeRangeEnum.lastYear;

export type FilterParams = {
  timeRange: FilterTimeRangeEnum;
  roleIds: string[];
  departmentIds: string[];
  stageNames: string[];
};

export const filtersDifferFromDefault = (filters: FilterParams): boolean => {
  const {
    timeRange,
    roleIds: selectedRoleIds,
    departmentIds: selectedDepartmentIds,
    stageNames: selectedStageNames,
  } = filters;
  return (
    _.concat(selectedRoleIds, selectedDepartmentIds, selectedStageNames)
      .length > 0 || timeRange !== DefaultFilterTimeRange
  );
};

const DATA_FOR_FEEDBACK_FILTERS = gql`
  query GetDataForFeedbackFilters(
    $zeusOnly: Boolean!
    $classicOrTransition: Boolean!
  ) {
    atsDepartments {
      id
      name
    }
    currentOrganization {
      id
      jobRoleGuideTemplates(where: { isArchived: false }) {
        id
        jobRoleGuideTemplateStages @skip(if: $classicOrTransition) {
          id
          name
        }
        stageTemplateInstallations @skip(if: $zeusOnly) {
          id
          atsStageName
        }
        jobRole {
          id
          name
        }
      }
    }
  }
`;

type SortBarProps = {
  filterParams: FilterParams;
  setFilterParams: React.Dispatch<React.SetStateAction<FilterParams>>;
  searchParams: string;
  setSearchParams: React.Dispatch<React.SetStateAction<string>>;
  clearFilters(): void;
  exportFeedback(format: "csv" | "xls"): Promise<void>;
};

function SortBar({
  filterParams,
  setFilterParams,
  searchParams,
  setSearchParams,
  clearFilters,
  exportFeedback,
}: SortBarProps) {
  const {
    timeRange,
    roleIds: selectedRoleIds,
    departmentIds: selectedDepartmentIds,
    stageNames: selectedStageNames,
  } = filterParams;
  const anyFiltersSet = filtersDifferFromDefault(filterParams);
  const {
    zeusInfo: { isZeus },
  } = useAuthContext();

  const { data: filterData } = useQuery<
    GetDataForFeedbackFilters,
    GetDataForFeedbackFiltersVariables
  >(DATA_FOR_FEEDBACK_FILTERS, {
    variables: {
      zeusOnly: isZeus,
      classicOrTransition: !isZeus,
    },
  });
  const jobRoles = useMemo(
    () =>
      _(filterData?.currentOrganization?.jobRoleGuideTemplates)
        .map("jobRole")
        .compact()
        .value(),
    [filterData?.currentOrganization?.jobRoleGuideTemplates]
  );
  const departments = filterData?.atsDepartments;

  const handleFilterChange = useCallback(
    (key: string, value: FilterTimeRangeEnum | string[]): void =>
      setFilterParams({
        ...filterParams,
        [key]: value,
      }),
    [filterParams, setFilterParams]
  );

  const stageNameGroups = useMemo(() => {
    if (isZeus) {
      return _(filterData?.currentOrganization?.jobRoleGuideTemplates)
        .flatMap("jobRoleGuideTemplateStages")
        .groupBy("name")
        .value();
    }
    return _(filterData?.currentOrganization?.jobRoleGuideTemplates)
      .flatMap("stageTemplateInstallations")
      .groupBy("atsStageName")
      .value();
  }, [filterData?.currentOrganization?.jobRoleGuideTemplates, isZeus]);

  const exportFeedbackCSVMemo = useEventCallback(() => exportFeedback("csv"));
  const exportFeedbackXLSMemo = useEventCallback(() => exportFeedback("xls"));

  return (
    <div className="flex w-full items-center">
      <TextField
        className="searchInput flex-shrink-0 mr-auto"
        placeholder="Search"
        type="text"
        icon={atlasSearch}
        value={searchParams}
        onChange={(value: string): void => setSearchParams(value)}
      />
      {anyFiltersSet && (
        <DeprecatedLink
          variant="monochrome"
          href="#"
          onPress={() => {
            clearFilters();
          }}
          className="flex-shrink-0 mx-6"
        >
          Clear all
        </DeprecatedLink>
      )}
      <ButtonCollection>
        <ButtonGroup isGhost isDropdown>
          <FakeMultiSelect
            minWidth="14rem"
            label="Department"
            items={useMemo(
              () =>
                departments?.map(
                  (dep): AtlasMenuContentItem => ({
                    _type: "item",
                    children: dep.name,
                    key: dep.id,
                    value: dep.id,
                  })
                ) || [],
              [departments]
            )}
            selectedIds={selectedDepartmentIds}
            onSelectedIdsChange={(value) =>
              handleFilterChange("departmentIds", value)
            }
          />
          <FakeMultiSelect
            minWidth="18rem"
            label="Job"
            items={useMemo(
              () =>
                jobRoles?.map(
                  (role): AtlasMenuContentItem => ({
                    _type: "item",
                    children: role.name,
                    key: role.id,
                    value: role.id,
                  })
                ) || [],
              [jobRoles]
            )}
            selectedIds={selectedRoleIds}
            onSelectedIdsChange={(value) =>
              handleFilterChange("roleIds", value)
            }
          />
          <FakeMultiSelect
            minWidth="18rem"
            label="Stage"
            items={useMemo(
              () =>
                Object.entries(stageNameGroups)?.map(
                  ([key, group]): AtlasMenuContentItem => ({
                    _type: "item",
                    children: `${key} ${
                      group.length > 1 ? `(${group.length})` : ""
                    }`,
                    key,
                    value: key,
                  })
                ) || [],
              [stageNameGroups]
            )}
            selectedIds={selectedStageNames}
            onSelectedIdsChange={(value) =>
              handleFilterChange("stageNames", value)
            }
          />
          <FeedbackTimeRangeFilter
            timeRange={timeRange}
            setFilter={(newValue) => handleFilterChange("timeRange", newValue)}
            className="flex-shrink-0"
          />
        </ButtonGroup>
        <Menu.Root>
          <Menu.Trigger>
            <Button isDropdown icon={atlasDownload}>
              Export
            </Button>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Item onSelect={exportFeedbackCSVMemo}>
              Export as .CSV
            </Menu.Item>
            <Menu.Item onSelect={exportFeedbackXLSMemo}>
              Export as .XLS
            </Menu.Item>
          </Menu.Content>
        </Menu.Root>
      </ButtonCollection>
    </div>
  );
}

export default SortBar;
